import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from '@shared/services/api/profile.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { ModalService } from '@shared/services/modal.service';
import { PasswordRegExp, PhoneNumberRegExp, resizeImage } from '@shared/utils';
import { confirmPasswordValidator } from '@shared/validators/confirmPassword.validator';
import { catchError, debounceTime, distinctUntilChanged, finalize, first, from, of, switchMap, take, tap } from 'rxjs';
import { ModalPersonalDataInfoComponent } from '../register/modal-personal-data-info/modal-personal-data-info.component';
import { ThemeService } from '../services/theme.service';
import { Profile } from '@shared/entities/profile';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrl: './account.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountComponent implements OnInit, OnDestroy {
  private _destroyRef = inject(DestroyRef);
  disableSaveBtn = true;
  private _originalPhone? = '';
  private _originalEmailNotifications? = false;
  private _phoneChanged = false;
  private _emailNotificationsChanged = false;
  private _profileId?: string;

  isErrorChangeProfile = false;
  isSuccessChangeProfile = false;
  isLoadingProfile = false;

  isLight = true;
  form!: FormGroup;
  isPasswordFieldsOpen = false;
  isErrorChangePassword = false;
  isSuccessChangePassword = false;
  isLoadingPassword = false;
  isErrorPassword = false;

  userImage?: string | null = null;
  isLoadingImage = false;
  imageLoadingSuccess?: boolean;

  showImageOverlay = false;
  imageDeletionSuccess?: boolean = undefined;

  @ViewChild('fileInput') fileInput: ElementRef = {} as ElementRef;

  get passwordsGroup() {
    return this.form.get('passwords') as FormGroup;
  }

  get passwordFormField() {
    return this.form.get('passwords')?.get('password');
  }

  get phoneFormField() {
    return this.form.get('phone');
  }

  get emailNotifications() {
    return this.form.get('emailNotifications');
  }

  constructor(
    public translate: TranslateService,
    private _themeService: ThemeService,
    private _formBuilder: FormBuilder,
    private _profileService: ProfileService,
    private _authenticationService: AuthenticationService,
    private _modalService: ModalService,
    private _cdr: ChangeDetectorRef
  ) {
    this.form = this._formBuilder.group({
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      role: [{ value: '', disabled: true }],
      accountName: [{ value: '', disabled: true }],
      phone: [{ value: '', disabled: true, validators: [Validators.pattern(PhoneNumberRegExp)] }],
      email: [{ value: '', disabled: true }],
      currentPassword: ['', [Validators.minLength(3)]],
      passwords: this._formBuilder.group(
        {
          password: ['', [Validators.required, Validators.pattern(PasswordRegExp)]],
          confirmPassword: ['', [Validators.required, Validators.pattern(PasswordRegExp)]]
        },
        { validators: confirmPasswordValidator }
      ),
      privacy: [{ value: '', disabled: true }],
      emailNotifications: [{ value: false }],
      img: [{ value: '', disabled: true }]
    });
  }

  ngOnInit() {
    this.form
      .get('currentPassword')
      ?.valueChanges.pipe(debounceTime(5000), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.isErrorChangePassword = false;
        this.isSuccessChangePassword = false;
      });

    this._profileService.profile$
      .pipe(
        first(),
        tap((profile) => {
          this.form.patchValue({
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            phone: profile.mobilePhone || profile.phone,
            role: profile.role,
            accountName: profile.accountName,
            emailNotifications: !profile.emailNotifications
          });
          this._profileId = profile.id;
          this._originalPhone = profile.mobilePhone || profile.phone;
          this._originalEmailNotifications = profile.emailNotifications;
          if (profile.photo) {
            this.userImage = profile.photo;
          }
          // this.form.disable();
        })
      )
      .subscribe();

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => (this.isLight = x))
      )
      .subscribe();

    this.form.valueChanges.subscribe((formValue) => {
      const { phone, emailNotifications, currentPassword } = formValue;
      this.isSuccessChangeProfile = false;
      this.isErrorChangeProfile = false;
      this._phoneChanged = phone !== this._originalPhone;
      this._emailNotificationsChanged = !emailNotifications !== this._originalEmailNotifications;
      const disabledFormProfile = !this._phoneChanged && !this._emailNotificationsChanged && !this.passwordsGroup.dirty;
      this.isErrorPassword = this.passwordsGroup.dirty && currentPassword === this.passwordFormField?.value;

      this.disableSaveBtn =
        this.form.pristine ||
        disabledFormProfile ||
        !this.phoneFormField?.valid ||
        (this.passwordsGroup.dirty && !this.passwordsGroup.valid) ||
        this.isErrorPassword;
    });
  }

  ngOnDestroy(): void {
    this.isErrorChangePassword = false;
    this.isSuccessChangePassword = false;
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const file: File | null = element.files ? element.files[0] : null;

    const originalPhoto = this.userImage;

    if (file) {
      this.userImage = undefined;
      this.isLoadingImage = true;
      this._cdr.markForCheck();
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          const base64 = reader.result as string;
          from(resizeImage(base64, 100, 100))
            .pipe(
              take(1),
              switchMap((resizedImage) => {
                const obj = { photo: resizedImage };
                return this._profileService.save(obj, this._profileId).pipe(
                  tap(() => {
                    this.userImage = reader.result as string;
                    this.imageLoadingSuccess = true;
                    this._profileService.refresh();
                    this.hideMessage();
                  }),
                  finalize(() => {
                    this.isLoadingImage = false;
                    this._cdr.markForCheck();
                  })
                );
              })
            )
            .subscribe({
              error: () => {
                this.userImage = originalPhoto;
                this.imageLoadingSuccess = false;
                this.hideMessage();
              }
            });
        }
      };
      reader.readAsDataURL(file);
    }
  }

  onClickChangePassword() {
    this.isPasswordFieldsOpen = !this.isPasswordFieldsOpen;
  }

  onSubmitForm() {
    const currentPassword = this.form.get('currentPassword')?.value;
    const newPassword = this.passwordFormField?.value;
    const willChangePwd = !!currentPassword && !!newPassword;

    if (this._phoneChanged || this._emailNotificationsChanged) {
      this.isLoadingProfile = true;
      const obj = new Profile();
      if (this._phoneChanged) {
        obj.phone = this.phoneFormField?.value;
      }
      if (this._emailNotificationsChanged) {
        obj.emailNotifications = !this.emailNotifications?.value;
      }

      this._profileService
        .save(obj, this._profileId)
        .pipe(
          first(),
          tap({
            next: () => {
              this._originalPhone = this.phoneFormField?.value;
              this._originalEmailNotifications = this.emailNotifications?.value;

              this.isErrorChangeProfile = false;
              this.isSuccessChangeProfile = true;
              this._profileService.refresh();
            },
            error: () => {
              this.isErrorChangeProfile = true;
              this.isSuccessChangeProfile = false;
            }
          }),
          finalize(() => {
            this.isLoadingProfile = false;
          })
        )
        .subscribe();
    }

    if (willChangePwd) {
      this.isLoadingPassword = true;
      this._authenticationService
        .changeUserPassword(newPassword, currentPassword)
        .pipe(
          first(),
          tap({
            next: () => {
              this.isSuccessChangePassword = true;
              this.isErrorChangePassword = false;
            },
            error: () => {
              this.isSuccessChangePassword = false;
              this.isErrorChangePassword = true;
            }
          }),
          finalize(() => {
            this.form.get('currentPassword')?.reset();
            this.passwordsGroup.reset();
            this.isLoadingPassword = false;
          })
        )
        .subscribe();
    }
  }

  onOpenModalInfo(event: Event) {
    event.stopPropagation();
    this._modalService.open(ModalPersonalDataInfoComponent, { showPdfLinks: true }, {
      size: 'lg'
    });
  }

  toggleOverlay() {
    if (!this.userImage) return;
    this.showImageOverlay = !this.showImageOverlay;
  }

  removeImage() {
    this.showImageOverlay = false;
    const originalPhoto = this.userImage;
    this.userImage = null;
    this.isLoadingImage = true;
    this._cdr.markForCheck();
    const obj = new Profile();
    obj.photo = '';
    this._profileService
      .save(obj, this._profileId)
      .pipe(
        tap(() => {
          this.imageDeletionSuccess = true;
          this._profileService.refresh();
          this.hideMessage();
        }),
        catchError(() => {
          this.userImage = originalPhoto;
          this.imageDeletionSuccess = false;
          this.hideMessage();
          return of();
        }),
        finalize(() => {
          this.isLoadingImage = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private hideMessage() {
    setTimeout(() => {
      this.imageLoadingSuccess = undefined;
      this.imageDeletionSuccess = undefined;
    }, 3000);
  }
}
